import React, { useEffect } from 'react';
import { AsyncResult } from 'react-use-async-result';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { UNSAFE_FEATURES } from '@/context/FeatureContext';

const Notification = window.Notification || {
  permission: 'unavailable',
  requestPermission: async () => 'unavailable',
};

type PermissionType = NotificationPermission | 'unavailable' | 'unknown';

type RxNotificationOptions = NotificationOptions & { onClick?: () => void };

const loadNotificationSound = () => {
  return new Promise<HTMLAudioElement>((resolve, reject) => {
    const audio = new Audio('/sounds/double_ping.mp3');
    audio.volume = 1.0;
    audio.addEventListener('canplaythrough', () => {
      resolve(audio);
    });
    audio.addEventListener('error', err => {
      reject(err);
    });
  });
};

let permissionStatus = 'unknown' as PermissionType;
const requestNotificationPermission = () => {
  console.log('Browser notifications: requesting permission...');
  if (!Notification?.requestPermission) {
    console.log('Browser notifications: unavailable');
    permissionStatus = 'unavailable';
    window.dispatchEvent(new Event('notificationPermissionStatusChanged'));
    return;
  }
  Notification.requestPermission().then(permission => {
    console.log('Browser notifications: permission:', permission);
    permissionStatus = permission;
    window.dispatchEvent(new Event('notificationPermissionStatusChanged'));
  });
};
document.addEventListener('DOMContentLoaded', () => {
  if (Notification.permission !== 'granted') {
    requestNotificationPermission();
  }
});

type NotificationState = {
  permission: PermissionType;
  lastTitle: string | null;
  sound: AsyncResult<HTMLAudioElement>;
};

/**
 * One notification state for the whole app (it was a react-singleton-hook).
 * Holds what every caller sees before the first caller mounts.
 */
const useNotificationStore = create<NotificationState>(() => ({
  permission: 'unknown',
  lastTitle: null,
  sound: AsyncResult.empty(),
}));

let started = false;

/** On first use: follow the permission status and load the notification sound once. */
const startNotifications = () => {
  if (started) {
    return;
  }
  started = true;
  useNotificationStore.setState({ permission: permissionStatus, sound: AsyncResult.pending() });
  window.addEventListener('notificationPermissionStatusChanged', () => {
    useNotificationStore.setState({ permission: permissionStatus });
  });
  AsyncResult.wrap(loadNotificationSound()).then(sound => useNotificationStore.setState({ sound }));
};

const setLastTitle = (lastTitle: string | null) => useNotificationStore.setState({ lastTitle });

export const useBrowserNotifications = () => {
  // Shallow-compared: notify() rewrites lastTitle with the same text, which re-rendered every reader.
  const { permission, lastTitle, sound } = useNotificationStore(
    useShallow(state => ({ permission: state.permission, lastTitle: state.lastTitle, sound: state.sound })),
  );
  useEffect(startNotifications, []);

  const notify = React.useCallback(
    (title: string, options?: RxNotificationOptions) => {
      if (permission != 'granted') {
        setLastTitle(`Error: no permission`);
        return;
      }

      const opts: RxNotificationOptions = {
        silent: true,
        icon: '/favicon.png',
        ...(options || {}),
      };

      const callback = opts.onClick;
      delete opts.onClick;

      try {
        const notif = new Notification(title, opts);
        notif.onclick = () => {
          callback?.();
        };
        notif.onerror = () => {
          setLastTitle(`Error sending: ${title}`);
          console.error('Browser notifications: error sending:', title);
        };
        setLastTitle(title);
        if (UNSAFE_FEATURES.notification_sound && sound.isDone) {
          sound.result?.play();
        }
        console.log('Browser notifications: sent:', title);
      } catch (e) {
        setLastTitle(`Error: ${e}: ${(e as any)?.message}`);
        console.error('Browser notifications: error sending:', title, e);
      }
    },
    [permission, sound],
  );

  return React.useMemo(
    () => ({
      permission,
      notify,
      requestPermission: requestNotificationPermission,
      lastTitle,
      sound,
    }),
    [permission, notify, lastTitle, sound],
  );
};
